import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { SITE_LOGO, SITE_NAV_LINKS } from '@bookkeeper/shared/site-chrome';
import { useSignupEntityType, useSignupUrl } from '@/hooks/useSignupUrl';
import { useSignInUrl } from '@/hooks/useSignInUrl';
import { trackLandingCtaClicked } from '@/lib/analytics';
import { useTranslation } from '@/i18n/i18n';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { SiteChromeLink } from '@/components/SiteChromeLink';

type NavigationProps = {
  showSignupCta?: boolean;
  /** Audience-specific header CTA (spokes): replaces the JDG signup button
      instead of leaving the slot empty (Interface Craft critique). */
  cta?: { label: string; href: string; trackId: string; external?: boolean };
};

// The items and their order are the shared www chrome (@bookkeeper/shared/site-chrome); the
// interpretation hub renders the same list. Roadmap nav item hidden 2026-07-03 (founder call,
// alongside the Fider hide — BOO-3231): the Roadmap section itself was later unmounted too (see
// the restore note in src/pages/Index.tsx).
const AUDIENCE_LINKS = SITE_NAV_LINKS.filter((link) => link.target.kind === 'page');
const CONTENT_LINKS = SITE_NAV_LINKS.filter((link) => link.target.kind !== 'page');

const desktopLinkClass =
  'text-sm font-medium text-foreground hover:text-primary transition-colors relative after:absolute after:bottom-0 after:left-0 after:size-0.5 after:bg-primary after:transition-all hover:after:w-full';
const mobileLinkClass =
  'flex min-h-11 items-center rounded-lg px-3 py-2 text-base font-medium text-foreground hover:text-primary transition-colors hover:bg-muted';

export const Navigation = ({ showSignupCta = true, cta }: NavigationProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const signupUrl = useSignupUrl({ source: 'nav' });
  const mobileSignupUrl = useSignupUrl({ source: 'nav_mobile' });
  const signupEntityType = useSignupEntityType();
  const signInUrl = useSignInUrl();
  const { t } = useTranslation();

  const trackSignupClick = (location: 'navigation_desktop' | 'navigation_mobile', label: string) =>
    trackLandingCtaClicked(location, label, { entity_type: signupEntityType });

  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
  };

  const mobileCta = cta ? (
    <Button size="lg" className="min-h-11 w-full font-bold" asChild>
      <a
        href={cta.href}
        target={cta.external ? '_blank' : undefined}
        rel={cta.external ? 'noopener noreferrer' : undefined}
        onClick={() => {
          trackLandingCtaClicked(`${cta.trackId}_mobile`, cta.label);
          handleLinkClick();
        }}
      >
        {cta.label}
        {cta.external && <span className="sr-only"> {t('nav.opensInNewTab')}</span>}
      </a>
    </Button>
  ) : showSignupCta ? (
    <Button size="lg" className="min-h-11 w-full font-bold" asChild>
      <a
        href={mobileSignupUrl}
        onClick={() => {
          trackSignupClick('navigation_mobile', t('nav.signupFree'));
          handleLinkClick();
        }}
      >
        {t('nav.signupFree')}
      </a>
    </Button>
  ) : null;

  return (
    <nav className="fixed top-0 left-0 right-0 z-[70] bg-card shadow-md transition-all">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href="/"
            className="flex min-h-11 min-w-11 items-center hover:opacity-80 transition-opacity"
          >
            <img
              src={SITE_LOGO.src}
              alt={SITE_LOGO.alt}
              className="h-10 md:h-12 w-auto dark:hidden"
            />
            <img
              src="/bookeeper-logo-dark.webp"
              alt={SITE_LOGO.alt}
              width={352}
              height={192}
              decoding="async"
              className="h-10 md:h-12 w-auto hidden dark:block"
            />
          </a>

          {/* Desktop Navigation links */}
          <div className="hidden xl:flex items-center gap-6 2xl:gap-8">
            {SITE_NAV_LINKS.map((link) => (
              <SiteChromeLink key={link.id} link={link} className={desktopLinkClass} />
            ))}
            {/* Login link */}
            <a href={signInUrl} className={desktopLinkClass}>
              {t('nav.login')}
            </a>

            {/* Language switcher */}
            <LanguageSwitcher />

            {cta ? (
              <Button size="default" className="font-bold" asChild>
                <a
                  href={cta.href}
                  target={cta.external ? '_blank' : undefined}
                  rel={cta.external ? 'noopener noreferrer' : undefined}
                  onClick={() => trackLandingCtaClicked(cta.trackId, cta.label)}
                >
                  {cta.label}
                  {cta.external && <span className="sr-only"> {t('nav.opensInNewTab')}</span>}
                </a>
              </Button>
            ) : (
              showSignupCta && (
                <Button size="default" className="font-bold" asChild>
                  <a
                    href={signupUrl}
                    onClick={() => trackSignupClick('navigation_desktop', t('nav.signup'))}
                  >
                    {t('nav.signup')}
                  </a>
                </Button>
              )
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="xl:hidden flex size-11 items-center justify-center rounded-lg text-foreground hover:text-primary transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="xl:hidden max-h-[calc(100dvh-4rem)] md:max-h-[calc(100dvh-5rem)] overflow-y-auto overscroll-contain border-t border-border py-3 pb-[calc(1rem+env(safe-area-inset-bottom))] animate-slide-in-right">
            <div className="flex flex-col gap-y-1">
              {AUDIENCE_LINKS.map((link) => (
                <SiteChromeLink
                  key={link.id}
                  link={link}
                  className={mobileLinkClass}
                  onClick={handleLinkClick}
                />
              ))}
              {mobileCta && <div className="py-2">{mobileCta}</div>}
              {CONTENT_LINKS.map((link) => (
                <SiteChromeLink
                  key={link.id}
                  link={link}
                  className={mobileLinkClass}
                  onClick={handleLinkClick}
                />
              ))}
              {/* Mobile login link */}
              <a href={signInUrl} className={mobileLinkClass} onClick={handleLinkClick}>
                {t('nav.login')}
              </a>

              {/* Mobile language switcher */}
              <div className="flex min-h-11 items-center px-3 py-2">
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
